import api from './api';
import { API_ENDPOINTS } from '../config/api';

export const wishlistService = {
  /**
   * Get user's wishlist from backend
   * Works for both logged-in users (JWT) and guest users (session)
   * @returns {Promise<Object>} Wishlist data with items
   */
  getWishlist: async () => {
    try {
      const response = await api.get(API_ENDPOINTS.WISHLIST.LIST);
      return {
        success: true,
        items: response.data.items || [],
        count: response.data.count || 0
      };
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      // Return empty wishlist on error
      if (error.response?.status === 404) {
        return {
          success: true,
          items: [],
          count: 0
        };
      }
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch wishlist',
        items: [],
        count: 0
      };
    }
  },

  /**
   * Toggle wishlist item (add if not present, remove if already in wishlist)
   * @param {number} productId - Product ID to toggle
   * @returns {Promise<Object>} Result with action (added/removed)
   */
  toggleWishlist: async (productId) => {
    try {
      const response = await api.post(API_ENDPOINTS.WISHLIST.TOGGLE, {
        product_id: productId
      });
      return {
        success: true,
        action: response.data.action, // 'added' or 'removed'
        message: response.data.message,
        item: response.data.item || null,
        data: response.data
      };
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update wishlist'
      };
    }
  },

  /**
   * Move wishlist item to cart
   * @param {number} wishlistItemId - Wishlist item ID to move
   * @param {number} quantity - Quantity to add to cart (default: 1)
   * @returns {Promise<Object>} Result with cart item details
   */
  moveToCart: async (wishlistItemId, quantity = 1) => {
    try {
      const endpoint = `${API_ENDPOINTS.WISHLIST.MOVE_TO_CART}/${wishlistItemId}/move_to_cart/`;
      const response = await api.post(endpoint, { quantity });
      return {
        success: true,
        message: response.data.message || 'Item moved to cart',
        cartItem: response.data.cart_item || null
      };
    } catch (error) {
      console.error('Error moving to cart:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to move item to cart'
      };
    }
  },

  /**
   * Merge current guest wishlist into logged-in user wishlist
   * Requires JWT token and guest session cookie
   */
  mergeGuestWishlist: async () => {
    try {
      const response = await api.post(API_ENDPOINTS.WISHLIST.MERGE_GUEST);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error merging guest wishlist:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to merge guest wishlist'
      };
    }
  },

  /**
   * Check if product is in wishlist
   * @param {number} productId - Product ID to check
   * @param {Array} wishlistItems - Current wishlist items
   * @returns {boolean} Whether product is in wishlist
   */
  isInWishlist: (productId, wishlistItems) => {
    if (!wishlistItems || !Array.isArray(wishlistItems)) {
      return false;
    }
    const normalizedProductId = productId !== undefined ? String(productId) : "";
    return wishlistItems.some(item =>
      String(item.product?.id) === normalizedProductId ||
      String(item.product_id) === normalizedProductId ||
      String(item.id) === normalizedProductId
    );
  }
};

export default wishlistService;
